import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/** Shared input style for the auth forms (Login / Setup). */
export const AUTH_INPUT =
  "border-input bg-white/[0.03] text-foreground w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-[border-color,box-shadow] focus-visible:border-accent-neon/60 focus-visible:ring-2 focus-visible:ring-[color-mix(in_oklab,var(--color-accent-neon)_35%,transparent)] disabled:opacity-50";

/**
 * Centered auth surface used by the Login + Setup pages: a brand mark, a
 * heading + subtitle, and the form body, on the deep terminal background.
 */
export default function AuthCard({
  title,
  subtitle,
  children,
  className,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <main className="auth-card relative flex min-h-dvh flex-col items-center justify-center overflow-hidden px-4 py-10">
      {/* Ambient halo behind the card. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 size-[36rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-accent-neon)_20%,transparent),transparent_60%)] blur-2xl"
      />
      <div
        className={cn(
          "glass bezel animate-fade-in-up relative w-full max-w-sm rounded-2xl border border-border/70 p-7",
          "before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-accent-neon/40 before:to-transparent",
          className,
        )}
      >
        <header className="mb-6 flex flex-col items-center gap-1.5 text-center">
          <div className="mb-1 flex items-center gap-2">
            <span
              className="pip-live size-2.5 rounded-full bg-accent-neon shadow-[0_0_10px_var(--color-accent-neon)]"
              aria-hidden
            />
            <strong className="text-gradient text-lg font-bold tracking-tight">
              DAX Bot
            </strong>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {subtitle && (
            <p className="text-muted-foreground text-sm">{subtitle}</p>
          )}
        </header>
        {children}
      </div>
    </main>
  );
}
